import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import {
  getFees,
  createFee,
  updateFee,
  deleteFee,
  getFeeById,
} from '../services/feeService';
import Notification from '../components/Common/Notification';

export const FeeContext = createContext();

export const FeeProvider = ({ children }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [fees, setFees] = useState([]);
  const [selectedFee, setSelectedFee] = useState(null);
  const [notification, setNotification] = useState({ message: '', isError: false });

  const fetchAuthHeaders = () => {
    const storedUser = localStorage.getItem('user');
    const token = user?.token || (storedUser ? JSON.parse(storedUser).token : '');

    if (!token) {
      console.error('User or token not available');
      return {};
    }

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  const showNotification = (message, isError) => {
    setNotification({ message, isError });
  };

  const closeNotification = () => {
    setNotification({ message: '', isError: false });
  };

  const fetchAllFees = async () => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getFees(authHeaders);
      setFees(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error fetching fees.';
      showNotification(errorMessage, true);
    }
  };

  const fetchFeeById = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getFeeById(id, authHeaders);
      setSelectedFee(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || `Error fetching fee with ID ${id}.`;
      showNotification(errorMessage, true);
    }
  };

  const addFee = async (feeData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const newFee = await createFee(feeData, authHeaders);
      setFees(prevFees => [...prevFees, newFee]);
      showNotification('Fee added successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error adding fee.';
      showNotification(errorMessage, true);
    }
  };

  const editFee = async (id, feeData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const updatedFee = await updateFee(id, feeData, authHeaders);
      setFees(prevFees => prevFees.map(fee => (fee.id === id ? updatedFee : fee)));
      showNotification('Fee updated successfully!', false);
      return updatedFee;
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error updating fee.';
      showNotification(errorMessage, true);
      throw error;
    }
  };

  const removeFee = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      await deleteFee(id, authHeaders);
      setFees(prevFees => prevFees.filter(fee => fee.id !== id));
      showNotification('Fee removed successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error removing fee.';
      showNotification(errorMessage, true);
    }
  };

  // useEffect(() => {
  //   fetchAllFees();
  // }, []);


  useEffect(() => {
    if (!isAuthenticated && !['/', '/login'].includes(window.location.pathname)) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);


  return (
    <FeeContext.Provider
      value={{
        fees,
        selectedFee,
        fetchAllFees,
        fetchFeeById,
        addFee,
        editFee,
        removeFee,
      }}
    >
      {children}
      {notification.message && (
        <Notification
          message={notification.message}
          isError={notification.isError}
          onClose={closeNotification}
        />
      )}
    </FeeContext.Provider>
  );
};

export const useFee = () => useContext(FeeContext);
